import { Fragment, useEffect, useState } from "react";
import produce from "immer";
import { compose } from "redux";
import { accountingConnector } from "../../../../services/accounting/connector";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import CardActions from "@material-ui/core/CardActions";
import TextField from "@material-ui/core/TextField";

import { Button } from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers/DatePicker";

import xlsx from "xlsx";
import { moneyFormater } from "../../../../utilities/formaters";

import "./InvoicePaymentImport.scss";

const InvoicePaymentImport = ({
  registerPayment,
  attachIntervals,
  accounting: { lastSentInvoicePaymentData },
}) => {
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [bankReceiveDate, setBankReceiveDate] = useState(new Date());

  useEffect(() => {
    if (lastSentInvoicePaymentData.sent) {
      const newList = produce(invoicePayments, (draft) => {
        const index = invoicePayments.findIndex(
          (item) => item.PaymentId === lastSentInvoicePaymentData.paymentId,
        );
        if (index > -1) {
          if (lastSentInvoicePaymentData.error)
            draft[index].Error = lastSentInvoicePaymentData.error;
          else if (lastSentInvoicePaymentData.notDistributableReason)
            draft[index].NotDistributableReason =
              lastSentInvoicePaymentData.notDistributableReason;
          else draft.splice(index, 1);
        }
      });
      setInvoicePayments(newList);
      const nextInvoice = getNextInvoiceToRegister(newList);
      if (nextInvoice) registerInvoicePayment(nextInvoice);
      else attachIntervals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSentInvoicePaymentData]);

  const totalInvoicePaymentsCount = invoicePayments.length;
  const totalInvoicePaymentsAmount = getInvoicePaymentsSum(invoicePayments);
  const isButtonEnabled = getNextInvoiceToRegister(invoicePayments); // If we can't get any invoice to register then disable button

  return (
    <div className="invoice-payment-import">
      <Card>
        <CardHeader
          title="Register Invoice Payments"
          subheader={"Import invoice payments from excelfile"}
        />
        <CardContent>
          {invoicePayments.length > 0 && (
            <div>
              <div className="import-information">
                <DatePicker
                  inputVariant="outlined"
                  className="small-datepicker"
                  value={bankReceiveDate}
                  onChange={setBankReceiveDate}
                  fullWidth={false}
                  label="Bank registered date"
                />
                <div>
                  <div>Invoice Payments: {totalInvoicePaymentsCount}</div>
                  <div>
                    Total Amount: {moneyFormater(totalInvoicePaymentsAmount, 2)}
                  </div>
                </div>
              </div>
              <div className="invoice-payment-grid">
                <div className="heading">Name</div>
                <div className="heading">OCR</div>
                <div className="heading">Payment Id</div>
                <div className="heading">Amount</div>
                <div className="heading">Error</div>
                <div className="heading">Remove</div>
                {invoicePayments.map((invoicePayment, index) => {
                  return (
                    <Fragment key={index}>
                      <div>{invoicePayment.SenderName}</div>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={invoicePayment.OCR}
                        onChange={(event) => {
                          changeImportInvoicePaymentOCR(index, event);
                        }}
                        fullWidth={true}
                        label="OCR"
                        error={!invoicePayment.HasValidOCR}
                      />
                      <TextField
                        size="small"
                        variant="outlined"
                        value={invoicePayment.PaymentId}
                        onChange={(event) => {
                          changeImportInvoicePaymentPaymentId(index, event);
                        }}
                        fullWidth={true}
                        label="PaymentId"
                        error={!invoicePayment.HasValidPaymentId}
                      />
                      <div>{moneyFormater(invoicePayment.Amount, 2)}</div>
                      <div className={"invoice-payment-grid-error-wrapper"}>
                        {getInvoicePaymentErrorTemplate(invoicePayment)}
                      </div>
                      <div>
                        <button onClick={() => removeItem(index)}>
                          Remove from list
                        </button>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
        <CardActions>
          <Button
            component="label"
            variant="contained"
            color="primary"
            id="importInvoicePayments"
          >
            <input
              id="fileImportInput"
              type="file"
              className="hidden-input"
              onChange={importInvoicePayments}
            />
            Import Invoice Payments
          </Button>
          <Button
            variant="contained"
            color="primary"
            id="registerInvoicePayments"
            onClick={initiateRegister}
            disabled={!isButtonEnabled}
          >
            Register Invoice Payments
          </Button>
        </CardActions>
      </Card>
    </div>
  );

  function removeItem(index) {
    const newList = produce(invoicePayments, (draft) => {
      draft.splice(index, 1);
    });
    setInvoicePayments(newList);
  }

  function changeImportInvoicePaymentOCR(invoicePaymentIndex, event) {
    let updatedInvoicePayments = produce(invoicePayments, (draft) => {
      let invoicePayment = draft[invoicePaymentIndex];
      invoicePayment.OCR = event.target.value;
      invoicePayment.HasValidOCR = validateOCR(invoicePayment.OCR);
      invoicePayment.Error = null;
      invoicePayment.NotDistributableReason = null;
    });

    setInvoicePayments(updatedInvoicePayments);
  }

  function changeImportInvoicePaymentPaymentId(invoicePaymentIndex, event) {
    let updatedInvoicePayments = produce(invoicePayments, (draft) => {
      let invoicePayment = draft[invoicePaymentIndex];
      invoicePayment.PaymentId = event.target.value;
      invoicePayment.HasValidPaymentId = validatePaymentId(
        invoicePayment.PaymentId,
      );
      invoicePayment.Error = null;
    });

    // PaymentIds are unique and must all be validated when an update occurs
    const validatedUpdatedList = validatePaymentIds(updatedInvoicePayments);
    setInvoicePayments(validatedUpdatedList);
  }

  function validatePaymentIds(updatedInvoicePayments) {
    return produce(updatedInvoicePayments, (draft) => {
      draft.forEach((invoicePayment) => {
        const isDuplicate =
          draft.filter((x) => x.PaymentId === invoicePayment.PaymentId).length >
          1;
        invoicePayment.HasValidPaymentId =
          !isDuplicate && validatePaymentId(invoicePayment.PaymentId);
      });
    });
  }

  function validateOCR(invoicePaymentOCR) {
    return invoicePaymentOCR.match(/^([\d]){13,15}$/);
  }

  function validatePaymentId(invoicePaymentPaymentId) {
    return invoicePaymentPaymentId.match(/^([\d]){12}$/);
  }

  function initiateRegister() {
    registerInvoicePayment(getNextInvoiceToRegister(invoicePayments));
  }

  function getNextInvoiceToRegister(_invoicePayments) {
    return _invoicePayments.find(
      (item) => !item.Error && !item.NotDistributableReason,
    );
  }

  function registerInvoicePayment(invoicePayment) {
    const data = {
      PaymentId: invoicePayment.PaymentId,
      Amount: Number(invoicePayment.Amount),
      OCR: invoicePayment.OCR,
      BankReceiveDate: bankReceiveDate,
    };
    registerPayment(data, "invoice");
  }

  function importInvoicePayments(event) {
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: "binary" });
      const workSheet = workbook.Sheets[workbook.SheetNames[0]];
      const headerRow = Number(
        Object.keys(workSheet)
          .filter((k) => k.startsWith("A"))
          .find((k) => workSheet[k].v === "Avsändare")
          .replace("A", ""),
      );
      const invoicePaymentOptions = {
        header: "",
        raw: true,
        range: headerRow - 1,
      };
      setBankReceiveDate(workSheet.A5.v);
      const invoicePaymentsJson = xlsx.utils.sheet_to_json(
        workSheet,
        invoicePaymentOptions,
      );
      let newInvoicePayments = [];
      for (let invoicePayment of invoicePaymentsJson) {
        const paymentId = invoicePayment["Bankgironummer/\r\nAvinummer"];
        const ocr = invoicePayment["Betalningsreferens"];
        const amount = invoicePayment["Belopp"]
          .toString()
          // Remove all formatting cases
          .replace(" ", "")
          .replace(",", ".");

        // There are some problems with the content of the excel file
        // it adds some empty rows with description of the file so we ignore any row
        // not including either an ocr, payment id or an amount
        if (ocr || paymentId || amount) {
          newInvoicePayments.push({
            SenderName: invoicePayment["Avsändare"],
            PaymentId: paymentId,
            OCR: ocr,
            HasValidOCR: validateOCR(invoicePayment["Betalningsreferens"]),
            Amount: Number(amount),
            Error: "",
            NotDistributableReason: "",
          });
        }
      }

      const amountSum = getInvoicePaymentsSum(newInvoicePayments);
      const numInvoicesField = `A${headerRow - 4}`;
      const invoiceTotalField = `B${headerRow - 4}`;
      if (
        Number(workSheet[numInvoicesField].v) !== newInvoicePayments.length ||
        Number(workSheet[invoiceTotalField].v) !== amountSum
      )
        alert("Non matching number of payments or amounts");
      // PaymentIds are unique and must all be validated when an update occurs
      const validatedNewList = validatePaymentIds(newInvoicePayments);
      setInvoicePayments(validatedNewList);
    };

    reader.readAsBinaryString(file);
  }

  function getInvoicePaymentsSum(list) {
    return list.reduce((a, b) => a + b.Amount * 100, 0) / 100; // *100 to avoid float issues, then /100 back again
  }

  function getInvoicePaymentErrorTemplate(invoicePayment) {
    if (invoicePayment.Error)
      return <div className={"error"}>{invoicePayment.Error}</div>;
    else if (invoicePayment.NotDistributableReason)
      return (
        <div className={"unmatchable"}>
          {invoicePayment.NotDistributableReason}
        </div>
      );

    return null;
  }
};

export default compose(accountingConnector)(InvoicePaymentImport);
